import { faDownload, faPen, faUpload } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// @ts-ignore — FileUploaderItem is exported at runtime but missing from @wfp/ui's index.d.ts
import { FileUploaderItem, Loading, Modal, Tab, Tabs, Tag } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import useCommon from "hooks/useCommon"
import { useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  backendAssetToFrontend,
  PolygonAttachResponse,
  useAttachAssetPolygonMutation,
  useDownloadAssetPolygonMutation,
  useGetAssetQuery,
} from "services/aimsBackend"
import AssetDetectionTab from "./AssetDetectionTab"
import LIATab from "./LIATab"
import AssetRegistrationTab from "./AssetRegistrationTab"

const POLYGON_WARNING_LABELS: Record<string, string> = {
  polygon_too_small: "Polygon is smaller than 0.8 ha",
  polygon_outside_buffer: "Polygon is more than 500 m from the asset coordinates",
  polygon_self_intersects: "Polygon self-intersects (attached anyway)",
  polygon_invalid_kmz: "KMZ file could not be parsed",
  polygon_no_polygon_in_kmz: "KMZ has no Polygon element",
}

function labelForPolygonWarning(code?: string): string {
  if (!code) return "Warning"
  return POLYGON_WARNING_LABELS[code] ?? code.replace(/_/g, " ")
}

function InfoRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="d-flex mb-2" style={{ gap: 8, fontSize: 14 }}>
      <p className="m-0" style={{ fontWeight: 700, minWidth: 120 }}>
        {label}
      </p>
      <p className="m-0">{value || "-"}</p>
    </div>
  )
}

export default function AssetPreviewPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { showSnackBar } = useCommon()
  const [selectedTab, setSelectedTab] = useState(0)

  const assetId = Number(id)
  const shouldFetchAsset = Number.isInteger(assetId)
  const { data: backendAsset, isLoading } = useGetAssetQuery(assetId, { skip: !shouldFetchAsset })
  const asset = useMemo(() => {
    return backendAsset ? backendAssetToFrontend(backendAsset) : null
  }, [backendAsset])

  // ── Polygon upload state ─────────────────────────────────────────
  const [showPolygonModal, setShowPolygonModal] = useState(false)
  const [kmzFile, setKmzFile] = useState<File | undefined>(undefined)
  const [polygonResult, setPolygonResult] = useState<PolygonAttachResponse | null>(null)
  const kmzInputRef = useRef<HTMLInputElement>(null)
  const [attachPolygon, { isLoading: isAttaching }] = useAttachAssetPolygonMutation()
  const [downloadAssetPolygon, { isLoading: isDownloadingPolygon }] = useDownloadAssetPolygonMutation()

  const openPolygonModal = () => {
    setKmzFile(undefined)
    setPolygonResult(null)
    if (kmzInputRef.current) kmzInputRef.current.value = ""
    setShowPolygonModal(true)
  }

  const handlePolygonUpload = async () => {
    if (!asset || !kmzFile) return
    try {
      const response = await attachPolygon({ id: asset.id, kmz: kmzFile }).unwrap()
      setPolygonResult(response)
      if (response.status === "valid") {
        showSnackBar("Polygon attached successfully", "success")
      } else if (response.status === "warning") {
        showSnackBar("Polygon was rejected — see details below", "warning")
      }
    } catch (err: any) {
      const data = err?.data as PolygonAttachResponse | undefined
      if (data?.error_summary) {
        const message = Object.entries(data.error_summary)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
          .join("; ")
        showSnackBar(message || "Failed to attach polygon", "error")
      } else {
        showSnackBar("Failed to attach polygon", "error")
      }
    }
  }

  const handlePolygonDownload = async () => {
    if (!asset) return
    try {
      const blob = await downloadAssetPolygon(asset.id).unwrap()
      if (!blob.size) {
        showSnackBar("No polygon file is available for this asset", "warning")
        return
      }
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = asset.geotrace || `${asset.assetId || `asset-${asset.id}`}.kmz`
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      showSnackBar("Failed to download polygon", "error")
    }
  }

  if (isLoading) {
    return (
      <div className="mt-4">
        <Loading active />
      </div>
    )
  }

  if (!asset) {
    return <p>Asset not found</p>
  }

  return (
    <div className="mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3 w-full">
        <h2 className="m-0">Asset Preview</h2>
        <div className="d-flex" style={{ gap: 8, marginLeft: "auto" }}>
          <Button action={openPolygonModal} label="Upload polygon" className="btn-full" icon={faUpload} />
          <Button
            action={handlePolygonDownload}
            label={isDownloadingPolygon ? "Downloading..." : "Download polygon"}
            className="btn-full"
            icon={faDownload}
            style={{ minWidth: 170 }}
          />
          <Button
            action={() => navigate(`/aims/AssetsView/edit/${asset.id}`)}
            label="Edit"
            className="btn-full blue"
            icon={faPen}
          />
        </div>
      </div>

      {/* Section 1: General Information */}
      <div className="bg-white p-4 mb-2" style={{ borderBottom: "1px solid #ddd" }}>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <InfoRow label="Asset ID" value={asset.assetId} />
            <InfoRow label="FLA" value={asset.fla} />
            <InfoRow label="Admin 1" value={asset.admin1} />
            <InfoRow label="Admin 2" value={asset.admin2} />
            <InfoRow label="Community" value={asset.community} />
            <InfoRow label="Villages" value={asset.villages} />
          </div>
          <div>
            <InfoRow label="Coordinates" value={`${asset.latY}, ${asset.longX}`} />
            <InfoRow label="Geotrace" value={asset.geotrace} />
            <InfoRow label="Start" value={`${asset.startMonth} ${asset.startYear}`} />
            <InfoRow label="End" value={`${asset.endMonth} ${asset.endYear}`} />
          </div>
        </div>
      </div>

      {/* Section 2: Asset Classification */}
      <div className="bg-white p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <InfoRow label="Category" value={asset.assetType} />
            <InfoRow label="Subcategory" value={asset.assetSubcat} />
            <InfoRow label="Asset Details" value={asset.assetDetail} />
            <InfoRow label="Extra Info" value={asset.extraInfo} />
          </div>
          <div>
            <InfoRow label="Output Unit" value={asset.outputUnit} />
            <InfoRow label="Project Size" value={asset.prjSize} />
            <InfoRow label="Outcomes" value={asset.outcomes} />
            <InfoRow label="Impact Area" value={asset.impactArea} />
          </div>
        </div>
      </div>

      {/* Section 3: Tabs */}
      <div className="bg-white p-4">
        <Tabs selected={selectedTab} onSelectionChange={((index: number) => setSelectedTab(index)) as any}>
          <Tab label="Asset Detections" href="" role="tab" selected={selectedTab === 0} tabIndex={0}>
            <AssetDetectionTab assetId={asset.id} active={selectedTab === 0} />
          </Tab>
          <Tab label="LIA" href="" role="tab" selected={selectedTab === 1} tabIndex={1}>
            <LIATab assetId={asset.id} active={selectedTab === 1} />
          </Tab>
          <Tab label="Community Insights" href="" role="tab" selected={selectedTab === 2} tabIndex={2}>
            <div className="mt-3">
              <p className="text-gray-400">No community insights available yet.</p>
            </div>
          </Tab>
          <Tab label="Asset Registration" href="" role="tab" selected={selectedTab === 3} tabIndex={3}>
            <AssetRegistrationTab assetId={asset.id} assetIso3={asset.country} />
          </Tab>
        </Tabs>
      </div>

      <Modal
        open={showPolygonModal}
        modalHeading="Attach polygon (KMZ)"
        primaryButtonText={isAttaching ? "Uploading..." : polygonResult ? "Done" : "Upload"}
        secondaryButtonText={polygonResult ? "Upload another" : "Cancel"}
        onRequestClose={() => setShowPolygonModal(false)}
        onSecondarySubmit={() => {
          if (polygonResult) {
            setKmzFile(undefined)
            setPolygonResult(null)
            if (kmzInputRef.current) kmzInputRef.current.value = ""
          } else {
            setShowPolygonModal(false)
          }
        }}
        onRequestSubmit={() => {
          if (polygonResult) {
            setShowPolygonModal(false)
          } else {
            handlePolygonUpload()
          }
        }}
        primaryButtonDisabled={!polygonResult && (!kmzFile || isAttaching)}
      >
        {!polygonResult && (
          <>
            <p className="m-0 mb-2" style={{ fontSize: 13, color: "#5a6872" }}>
              Upload a single <code>.kmz</code> file. On success the polygon replaces the current one and the impact
              area is recomputed. Polygons under 0.8 ha or further than 500 m from the asset coordinates will be
              rejected with a warning.
            </p>

            <input
              ref={kmzInputRef}
              type="file"
              accept=".kmz"
              style={{ display: "none" }}
              onChange={e => setKmzFile(e.target.files?.[0])}
            />

            {kmzFile ? (
              <FileUploaderItem
                uuid={kmzFile.name}
                name={kmzFile.name}
                status="edit"
                iconDescription="Remove file"
                onDelete={() => {
                  setKmzFile(undefined)
                  if (kmzInputRef.current) kmzInputRef.current.value = ""
                }}
              />
            ) : (
              <button
                type="button"
                onClick={() => kmzInputRef.current?.click()}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  border: "1px solid #007dbc",
                  borderRadius: 4,
                  background: "#fff",
                  color: "#007dbc",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                <FontAwesomeIcon icon={faUpload} />
                Choose .kmz file
              </button>
            )}
          </>
        )}

        {polygonResult && (
          <div>
            <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
              <Tag
                type={
                  polygonResult.status === "valid"
                    ? "success"
                    : polygonResult.status === "warning"
                    ? "warning"
                    : "error"
                }
                style={{ marginBottom: 0 }}
              >
                {polygonResult.status}
              </Tag>
              {polygonResult.detail.impact_area && (
                <span style={{ fontSize: 13 }}>
                  Impact area: <b>{polygonResult.detail.impact_area}</b>
                </span>
              )}
            </div>

            {polygonResult.status === "valid" && polygonResult.detail.warnings.length === 0 && (
              <p className="m-0" style={{ fontSize: 13 }}>
                Polygon attached successfully.
              </p>
            )}

            {polygonResult.detail.warnings.length > 0 && (
              <div>
                <p className="m-0 mb-1" style={{ fontWeight: 700, fontSize: 13 }}>
                  Warnings ({polygonResult.detail.warnings.length})
                </p>
                <ul
                  style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}
                >
                  {polygonResult.detail.warnings.map((w: any) => (
                    <li
                      key={`${w.warning ?? "warn"}::${w.detail ?? ""}`}
                      style={{
                        background: "#fff4e0",
                        border: "1px solid #b86200",
                        borderRadius: 6,
                        padding: "8px 12px",
                        fontSize: 13,
                      }}
                    >
                      <p className="m-0" style={{ fontWeight: 700, color: "#b86200" }}>
                        {labelForPolygonWarning(w.warning)}
                      </p>
                      {w.detail && (
                        <p className="m-0" style={{ marginTop: 2 }}>
                          {w.detail}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
